import { useState, useEffect } from "react";
import { Image, Stack, useToast } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import TableStructure from "../../CommonComponents/TableStructure";
import { getImageURL } from "../../util/getImage";
import { getThemeObjectData } from "../../services/Theme";
import { handleExport as handleExportUtil } from '../../util/helper';
import { useFilterPage } from "../../hooks/useFilterPage";
import { FilterBox } from "../../CommonComponents/Filter/FilterBox";
import { explore } from "../../constants/constants";

const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const placeholderImg = `${IMAGE_BASE_PATH}/ui/DummyImage/small.png`;

const THEME_FILTER_STORAGE_KEY = "themeTable_filterState";

let _pendingRestore = false;

const ThemeObjectTable = () => {
  const [sortOrder, setSortOrder] = useState("asc");
  const session = useSelector((state) => state.session);
  const limit = 50;
  const toast = useToast();
  const location = useLocation();

  const shouldRestore = _pendingRestore || location.state?.restoreFilters === true;
  _pendingRestore = false;
  if (!shouldRestore) {
    sessionStorage.removeItem(THEME_FILTER_STORAGE_KEY);
  }

  const citationFactor = (data) => {
    return data?.map((item) => ({
      name: item?.name,
      alias: item?.alias,
      direct_url_of_cover_image: item?.image_alias,
      year: item?.release_date,
      masterlist_type: item?.masterlist_type,
    }));
  };

  const normalizeResult = (raw) => {
    if (raw?.records?.data !== undefined) {
      return {
        records: citationFactor(raw.records.data),
        total: raw.records.total ?? 0,
        facetsHeaders: raw?.facetsHeaders,
        facets: raw?.facets,
        pageNumber: raw.records.currentPage,
      };
    }
    return {
      records: citationFactor(raw?.data || []),
      total: raw?.total ?? 0,
      facetsHeaders: raw?.facetsHeaders,
      facets: raw?.facets,
      pageNumber: raw?.currentPage,
    };
  };

  const {
    records,
    currentPage,
    totalPages,
    totalRecords,
    loading,
    searchText, setSearchText,
    facets,
    setApply,
    filterCount,
    filteredResultCount,
    showFilter, setShowFilter,
    filterPills, setFilterPills,
    filterOptions, setFilterOptions,
    handleSearch,
    handleClear,
    handlePageChange,
    handleCheckboxChange,
    handleReset,
  } = useFilterPage({
    limit,
    fetchFn: getThemeObjectData,
    normalizeResult,
    storageKey: THEME_FILTER_STORAGE_KEY,
  });

  useEffect(() => {
    return () => {
      _pendingRestore = window.location.pathname.startsWith('/theme/');
    };
  }, []);

  const sortRecords = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);
    setFilterOptions((prev) => ({ ...prev, sortBy_v: "name", sortOrder_v: newSortOrder }));
    setApply(true);
  };

  const handleRowExport = (record) => {
    const moduleInputs = {
      type: "Theme",
      alias: record.alias
    };
    handleExportUtil("masterlist", toast, moduleInputs);
  };

  const columns = [
    { label: "Alias", accessor: "alias" },
    { label: "Name", accessor: "name" },
    { label: "Masterlist Sub-Type", accessor: "masterlist_type" },
    {
      label: "Image",
      accessor: "direct_url_of_cover_image",
      render: (value) =>
        <Image
          src={getImageURL(value, "200", "100")}
          width="200px"
          height="100px"
          objectFit="contain"
          fallbackSrc={placeholderImg}
          alt="Cover image"
        />
    }
  ];

  return (
    <>
      <TableStructure
        isAuthenticated={session?.isAuthenticated}
        handleSearch={handleSearch}
        handleClear={handleClear}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        totalPages={totalPages}
        sortRecords={sortRecords}
        sortOrder={sortOrder}
        loading={loading}
        records={records}
        type="theme"
        title="Theme"
        totalRecords={totalRecords}
        columns={columns}
        onRowExport={handleRowExport}
        filterOptions={filterOptions}
        handleCheckboxChange={handleCheckboxChange}
        setApply={setApply}
        filterPills={filterPills}
        setFilterOptions={setFilterOptions}
        setSearchTerm={setSearchText}
        setFilterPills={setFilterPills}
        filteredResultCount={filteredResultCount}
        filterCount={filterCount}
        onFilterToggle={facets.length > 0 ? () => setShowFilter(!showFilter) : undefined}
        showFilter={showFilter}
        filterAvailble={facets.length > 0}
        filterPanel={showFilter && facets?.length > 0 && (
          <Stack
            marginRight={"0px!important"}
            ml={3}
            right={"0px"}
            top={"0px"}
            pos={'sticky'}
            pt="0"
          >
            <FilterBox
              setApply={setApply}
              setShowFilterBox={setShowFilter}
              explore={explore}
              handleCheckboxChange={handleCheckboxChange}
              handleSearch={handleSearch}
              filterOptions={filterOptions}
              filteroptionData={facets}
              setFilterOptions={setFilterOptions}
              setSearchTerm={setSearchText}
              filterCount={filterCount}
              searchTerm={searchText}
              handleReset={handleReset}
              className="box-filter-theme"
              showSearch={true}
            />
          </Stack>
        )}
      />
    </>
  );
};

export default ThemeObjectTable;
